import * as React from 'react';
import { Fade } from 'react-reveal';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import { WhyOrigin } from './components/WhyOrigin';

import {
  Container,
  StretchContainer,
  StretchDigitalAdoptionContainer,
  StretchMidPageCompaignContainer,
} from './components/Container';

import { QuickLinks } from './components/QuickLinks';
import { FindPlan, FindPlanPure } from './components/FindPlan';
import { HelpSupport } from './components/HelpSupport';
import { CardBlockContainer } from './components/Card/CardBlockContainer';
import { BannerResponsive } from './components/BannerResponsive';

import { IHomepageStore } from './models/content.interfaces';
import { DigitalAdoptionCampaign } from './components/DigitalAdoptionCampaign';
import { MidPageCampaign } from './components/MidpageCampaign';
import { SignUpPure } from './components/SignUp';

import { MediaQueryProvider } from 'react-media-query-hoc';
import { CONFIG } from './consts/mediaQuery.js';

import { configAnalytics, navigation } from '@origin-digital/reporting-client';
import { Disclaimer } from './components/Disclaimer';
import { Animation } from './components/Animation';
import { signupData } from './appData';
import { ProofPoints } from './components/ProofPoints';
import { ExperienceProvider, ExperienceSelector } from '@origin-digital/features';

const SHOW_ANIMATIONS = true;

export const WhyOriginContainer = styled(Container)`
  padding: 40px 0;
`;

export const mboxes = {
  bannerMbox: 'for-home:GraphicBanner',
  quickLinksMbox: 'for-home:QuickLinks',
  whyOriginMbox: 'for-home:CardTopLight',
  topCardsMbox: 'for-home:CardTop',
  disclaimerMbox: 'for-home:disclaimer',
  customerAbTestMbox: 'OE-411-mbox',
  prospectAbTestMbox: 'OE-414-mbox',
};

export const appName = 'origin-homepage';

const CustomerExperienceB = ({ store, experienceContent }) => (
  <>
    <WhyOriginContainer>
      <ProofPoints {...experienceContent} />
    </WhyOriginContainer>
    {store.content.midpagecampaign && (
      <StretchMidPageCompaignContainer>
        <MidPageCampaign {...store.content.midpagecampaign} />
      </StretchMidPageCompaignContainer>
    )}
    <ExperienceSelector mbox={mboxes.topCardsMbox}>
      {(content) => (
        <CardBlockContainer
          {...store.content.topcard}
          {...content}
          trackingPrefix="cardTop"
          data-comment="CardTop"
        />
      )}
    </ExperienceSelector>
    {store.content.bottomcard && (
      <CardBlockContainer
        trackingPrefix="cardBottom"
        data-comment="CardBottom"
        {...store.content.bottomcard}
      />
    )}
  </>
);

const ProspectAbTest = ({ store }) => (
  <ExperienceSelector mbox={mboxes.prospectAbTestMbox}>
    {(experienceContent) => {
      const isExperienceB = !isEmpty(experienceContent);
      return isExperienceB ? (
        <WhyOriginContainer>
          <ProofPoints {...experienceContent} />
        </WhyOriginContainer>
      ) : (
        <ExperienceSelector mbox={mboxes.whyOriginMbox}>
          {(content) => (
            <WhyOriginContainer className="why-origin-container">
              <WhyOrigin {...(store.content as any).toplightcard} {...content} />
            </WhyOriginContainer>
          )}
        </ExperienceSelector>
      );
    }}
  </ExperienceSelector>
);

const ControlExperience = ({ store, signUp }) => (
  <>
    {store.content.plan && (
      <StretchContainer>
        <FindPlan
          {...store.content.plan}
          component={FindPlanPure}
          dataComment={'FindPlan'}
          trackingId={'header-banner'}
          trackingLabel={'Show me good energy'}
        />
      </StretchContainer>
    )}
    <ExperienceSelector mbox={mboxes.topCardsMbox}>
      {(content) => (
        <CardBlockContainer
          {...store.content.topcard}
          {...content}
          trackingPrefix="cardTop"
          data-comment="CardTop"
        />
      )}
    </ExperienceSelector>
    {store.content.midpagecampaign && (
      <StretchMidPageCompaignContainer>
        <MidPageCampaign {...store.content.midpagecampaign} />
      </StretchMidPageCompaignContainer>
    )}
    {store.content.digitaladoption && (
      <StretchDigitalAdoptionContainer>
        <DigitalAdoptionCampaign {...store.content.digitaladoption} />
      </StretchDigitalAdoptionContainer>
    )}
    {store.content.bottomcard && (
      <CardBlockContainer
        trackingPrefix="cardBottom"
        data-comment="CardBottom"
        {...store.content.bottomcard}
      />
    )}
    <StretchMidPageCompaignContainer>
      <FindPlan
        {...signUp}
        component={SignUpPure}
        dataComment={'SignUp'}
        trackingId={'footer-banner'}
        trackingLabel={'See energy plans'}
      />
    </StretchMidPageCompaignContainer>
  </>
);

export class App extends React.Component<{ store: IHomepageStore }, {}> {
  public componentDidMount() {
    configAnalytics({ appName });
    navigation('screen', {
      appName,
      currentUri: window.location.href,
      friendlyUri: 'originenergy:for-home',
    });
  }

  public render() {
    const { store } = this.props;
    const signUp = isEmpty(store.content.signup) ? signupData : store.content.signup;

    return (
      <ExperienceProvider mboxes={Object.values(mboxes)}>
        <MediaQueryProvider queries={CONFIG}>
          <ExperienceSelector mbox={mboxes.bannerMbox}>
            {(content) => (
              <BannerResponsive
                {...store.content.banner}
                {...content}
                showDisclaimer
                verticalOffset={40}
              />
            )}
          </ExperienceSelector>
          <ExperienceSelector mbox={mboxes.quickLinksMbox}>
            {(content) => (
              <Animation
                enabled={SHOW_ANIMATIONS}
                component={Fade}
                ssrFadeout={true}
                effect="fadeInUp"
                duration={350}
                bottom
                ssrReveal
                distance="20%"
              >
                <QuickLinks {...store.content.quicklinks} {...content} verticalOffset={-32} />
              </Animation>
            )}
          </ExperienceSelector>
          <ExperienceSelector mbox={mboxes.customerAbTestMbox}>
            {(content) => {
              const isExperienceB = !isEmpty(content);
              return isExperienceB ? (
                <CustomerExperienceB store={store} experienceContent={content} />
              ) : (
                <>
                  <ProspectAbTest store={store} />
                  <ControlExperience store={store} signUp={signUp} />
                </>
              );
            }}
          </ExperienceSelector>
          {store.content.helpsupport && (
            <StretchContainer baseSideMargin="0" last>
              <HelpSupport {...store.content.helpsupport} />
            </StretchContainer>
          )}
          <ExperienceSelector mbox={mboxes.disclaimerMbox}>
            {(content) =>
              content && (
                <Container>
                  <Disclaimer {...content} />
                </Container>
              )
            }
          </ExperienceSelector>
        </MediaQueryProvider>
      </ExperienceProvider>
    );
  }
}
